import {
  ComposerInfoJSONResponse,
  VTEXCollectionResponse,
  ComposerCommissionSalesSummary,
  ComposerCommissionSalesDetail,
  ComposerProductGroupSummary,
} from "myscore_models/lib/MyscoreResponseTypes.js";
import { PatchComposerInfoJSONPayload } from "myscore_models/lib/MyscoreRequestTypes.js";
import { AuthenticatedFetch } from "./Requests";
import { UploadResponse } from "./interfaces";
import { CollectionProduct } from "myscore_models/src/MyscoreResponseTypes";
import type { SharedCatalogProduct } from "myscore-shared-components";

/**
 * Gets the profile of the currently logged in user.
 * @returns {Promise<ComposerInfoJSONResponse>}
 */
export async function getProfile(): Promise<ComposerInfoJSONResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/profile`
  );
  const resJson: { composer: ComposerInfoJSONResponse } = await res.json();
  return resJson.composer;
}

export async function isProfileWebPathAvailable(
  webPath: string
): Promise<boolean> {
  const res = await AuthenticatedFetch(
    `${
      process.env.VUE_APP_MYSCORE_BACKEND
    }/api/v1/profile/webPathAvailable?webPath=${encodeURIComponent(webPath)}`
  );
  if (!res.ok) {
    throw new Error("Failed to check web path availability");
  }
  const resJson: { available: boolean } = await res.json();
  return resJson.available;
}

export type GetBrandProductsParams = {
  skuIds?: string[];
  productGroupIds?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: "created" | "title";
  sortDir?: "asc" | "desc";
  search?: string;
  webStatus?: "all" | "active" | "inactive";
};

export type BrandProductsResponse = {
  products: SharedCatalogProduct[];
  page: number;
  pageSize: number;
  total: number;
};

export async function getBrandProducts(
  params: GetBrandProductsParams = {}
): Promise<BrandProductsResponse> {
  const query = new URLSearchParams();
  query.set("webStatus", params.webStatus || "all");
  if (params.productGroupIds?.length) {
    query.set("productGroupIds", params.productGroupIds.join(","));
  } else if (params.skuIds?.length) {
    query.set("skuIds", params.skuIds.join(","));
  }
  if (params.page) {
    query.set("page", String(params.page));
  }
  if (params.pageSize) {
    query.set("pageSize", String(params.pageSize));
  }
  if (params.sortBy) {
    query.set("sortBy", params.sortBy);
  }
  if (params.sortDir) {
    query.set("sortDir", params.sortDir);
  }
  const search = params.search?.trim();
  if (search) {
    query.set("search", search);
  }

  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/products?${query.toString()}`
  );
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch catalog products");
  }
  return data;
}

/**
 * Updates attributes of the profile of the currently logged in user.
 * @returns {Promise<ComposerInfoJSONResponse>}
 */
export async function updateProfile(
  payload: PatchComposerInfoJSONPayload
): Promise<ComposerInfoJSONResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/profile`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  const resJson: { composer: ComposerInfoJSONResponse } = await res.json();
  return resJson.composer;
}

/**
 * Get products in a VTEX collection
 * @returns {Promise<VTEXCollectionResponse>}
 */
export async function getCollectionProducts(
  vtexCollectionId: string
): Promise<VTEXCollectionResponse> {
  if (!vtexCollectionId) {
    throw new Error("No collection ID provided.");
  }
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/collection/${vtexCollectionId}`
  );
  const resJson = await res.json();
  if (!res.ok) {
    throw new Error(resJson.message || "Failed to fetch collection products");
  }
  return resJson.collection as VTEXCollectionResponse;
}

/**
 * Add products to a VTEX collection based on their pepper numbers (Product Group ID/ProductRefId)
 * @returns {Promise<VTEXCollectionResponse>}
 */
export async function addCollectionProducts(
  vtexCollectionId: string,
  pepperNumbers: string[],
  reordering = false
): Promise<VTEXCollectionResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/collection/${vtexCollectionId}/addProducts?reordering=${reordering}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ pepperNumbers }),
    }
  );
  const resJson = await res.json();
  if (!res.ok) {
    throw new Error(resJson.message || "Failed to add products to collection");
  }
  const collection: VTEXCollectionResponse = resJson.collection;
  return collection;
}

/**
 * Removes products from a VTEX collection based on their pepper numbers (Product Group ID/ProductRefId)
 * @returns {Promise<VTEXCollectionResponse>}
 */
export async function removeCollectionProducts(
  vtexCollectionId: string,
  pepperNumbers: string[]
): Promise<VTEXCollectionResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/collection/${vtexCollectionId}/deleteProducts`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ pepperNumbers }),
    }
  );
  const resJson = await res.json();
  if (!res.ok) {
    throw new Error(
      resJson.message || "Failed to remove products from collection"
    );
  }
  const collection: VTEXCollectionResponse = resJson.collection;
  return collection;
}

/**
 * Uploads the profile picture of the currently logged in composer.
 * @param file - The file to upload. Needs to be a valid image file in PNG, or JPEG format.
 */
export async function uploadProfilePicture(
  file: File
): Promise<ComposerInfoJSONResponse> {
  // Ensure file is an image (PNG or JPEG)
  if (!["image/png", "image/jpeg"].includes(file.type)) {
    throw new Error("Invalid file type. Please upload a PNG or JPEG image.");
  }

  const imageFormat = file.type === "image/png" ? "png" : "jpg";
  let res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/profile/photo/getUploadURL?imageFormat=${imageFormat}`
  );

  if (!res.ok) {
    throw new Error("Failed to get upload URL.");
  }

  const { uploadURL } = await res.json();

  // Upload the image to the CDN
  res = await fetch(uploadURL, {
    method: "PUT",
    body: file,
  });

  if (!res.ok) {
    throw new Error("Failed to upload image.");
  }

  // Set the image format in the profile
  res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/profile/photo/setImageFormat`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ imageFormat }),
    }
  );

  if (!res.ok) {
    throw new Error("Failed to set image format.");
  }

  const resJson: { composer: ComposerInfoJSONResponse } = await res.json();
  return resJson.composer;
}

/**
 * Get sales summary, split into POD and ePrint sales
 * @returns {Promise<ComposerCommissionSalesSummary>}
 */
export async function getSalesSummary(
  start: Date,
  end: Date
): Promise<ComposerCommissionSalesSummary> {
  const res = await AuthenticatedFetch(
    `${
      process.env.VUE_APP_MYSCORE_BACKEND
    }/api/v1/sales/summary?startDateTime=${encodeURIComponent(
      start.toISOString()
    )}&endDateTime=${encodeURIComponent(end.toISOString())}`
  );
  return await res.json();
}

/**
 * Get paginated sales for the sales table.
 * @returns {Promise<ComposerCommissionSalesDetail>}
 */
export async function getSalesDetail(
  start: Date,
  end: Date,
  offset?: number
): Promise<ComposerCommissionSalesDetail> {
  const res = await AuthenticatedFetch(
    `${
      process.env.VUE_APP_MYSCORE_BACKEND
    }/api/v1/sales/detail?startDateTime=${encodeURIComponent(
      start.toISOString()
    )}&endDateTime=${encodeURIComponent(end.toISOString())}${
      offset ? `&offset=${offset}` : ""
    }`
  );
  return await res.json();
}

/**
 * Get paginated product groups for a composer, used for Catalog page.
 * To get featured products, first get the featured collection ID from the composer's profile,
 * then use that ID to get the products in that collection from this function.
 * @returns {Promise<ComposerProductGroupSummary>}
 */
export async function getProductGroups(
  sortBy?: "title" | "createdDate",
  sortDirection?: "asc" | "desc",
  search?: string,
  vtexCollectionId?: string,
  offset?: number,
  limit?: number,
  featuredOnly?: boolean
): Promise<ComposerProductGroupSummary> {
  const query = new URLSearchParams();
  if (sortBy) query.append("sortBy", sortBy);
  if (sortDirection) query.append("sortDirection", sortDirection);
  if (search) query.append("search", search);
  if (vtexCollectionId)
    query.append("vtexCollectionId", vtexCollectionId.toString());
  if (offset !== undefined) query.append("offset", offset.toString());
  if (limit) query.append("limit", limit.toString());
  if (featuredOnly) query.append("featuredOnly", featuredOnly.toString());
  const res = await AuthenticatedFetch(
    `${
      process.env.VUE_APP_MYSCORE_BACKEND
    }/api/v1/productGroups?${query.toString()}`
  );
  return await res.json();
}

export interface CodedField {
  code: string;
  description: string | null;
  primaryFlag?: boolean;
}

type DeliveryMethod =
  | "Ships from J.W. Pepper"
  | "Print Immediately in My Account"
  | "Digital Download in My Account"
  | "Purchased Item will be Emailed"
  | "Ships Direct from Manufacturer";

export interface BrandProductSkuPart {
  id: number;
  name: string;
  price: number;
  imageUrl: string;
}

export interface BrandSku {
  skuId: string;
  deliveryMethod?: DeliveryMethod;
  scoreAvailable?: boolean;
  audioAvailable?: boolean;
  /** Omitted by the API when this SKU has no associated parts. */
  parts?: BrandProductSkuPart[];
  partsSoldSeparately?: boolean;
  /** Bridge `pubId`; supplier-facing id (shown as Supplier ID in the editor). */
  pubId?: string | null;
  productLine: CodedField;
  format: CodedField;
  styles: CodedField[];
  voices: CodedField[];
  grades: CodedField[];
  listPrice: number;
  minimumOrderQuantity: number;
  modified: Date;
  upc?: string;
  isbn?: string;
  ean?: string;
  youtubeUrl?: string;
  /** `A` active on web, `M` masked */
  webStatus?: "A" | "M";
}

interface BrandSkuEditPayload {
  skuId: string;
  format: CodedField;
  styles: CodedField[];
  voices: CodedField[];
  grades: CodedField[];
  listPrice: number;
  minimumOrderQuantity: number;
  upc: string;
  isbn: string;
  ean: string;
  youtubeUrl: string;
  webStatus: "A" | "M";
}

export interface BrandProduct {
  title: string;
  subtitle: string;
  composer: string;
  arranger: string;
  lyricist?: string;
  artist?: string;
  description: {
    copy: string;
    locked: boolean;
  };
  // marketingTags: string[];
  skus: BrandSku[];
  modified: Date;
}

export interface BrandProductEditPayload {
  title: string;
  subtitle: string;
  composer: string;
  arranger: string;
  lyricist: string;
  artist: string;
  description: string;
  // marketingTags: string[];
  skus: BrandSkuEditPayload[];
}

export async function getProductPayloadForEdit(
  productId: string
): Promise<BrandProduct> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/product/${encodeURIComponent(
      productId
    )}/edit`
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(errBody.message || res.statusText || "Request failed");
  }
  return (await res.json()) as BrandProduct;
}

export async function putProductEdit(
  productId: string,
  body: BrandProductEditPayload
): Promise<void> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/product/${encodeURIComponent(
      productId
    )}/edit`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(errBody.message || res.statusText || "Save failed");
  }
}

export async function putProductSkuParts(
  productId: string,
  skuId: string,
  parts: BrandProductSkuPart[]
): Promise<{ parts: BrandProductSkuPart[] }> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/product/${encodeURIComponent(
      productId
    )}/sku/${encodeURIComponent(skuId)}/parts`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ parts }),
    }
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(
      errBody.message || res.statusText || "Unable to save parts"
    );
  }
  return (await res.json()) as { parts: BrandProductSkuPart[] };
}

export async function deleteProductEdit(productId: string): Promise<void> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/product/${encodeURIComponent(
      productId
    )}/edit`,
    { method: "DELETE" }
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(
      errBody.message || res.statusText || "Unpublish request failed"
    );
  }
}

export async function deleteProductSkuEdit(
  productId: string,
  skuId: string
): Promise<void> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/product/${encodeURIComponent(
      productId
    )}/sku/${encodeURIComponent(skuId)}/edit`,
    { method: "DELETE" }
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(
      errBody.message || res.statusText || "SKU unpublish request failed"
    );
  }
}

export interface ProductLineFacetCode {
  code: string;
  description: string;
}

export interface ProductLineFormat extends ProductLineFacetCode {
  websiteLabel: string;
  ensemble?: string;
  accompaniment?: string;
}

export interface ProductLineVoice extends ProductLineFacetCode {
  websiteLabel: string;
  instrument?: string;
  /** When true, voice description is included in synthesized website-style copy. */
  usedInWebsiteDescription?: boolean;
}

export interface ProductLineWithFacets {
  code: string;
  description: string;
  minimumPrice: number;
  formats: ProductLineFormat[];
  grades: ProductLineFacetCode[];
  styles: ProductLineFacetCode[];
  voices: ProductLineVoice[];
}

export interface ProductCodesResponse {
  productLines: ProductLineWithFacets[];
}

let productCodesMemoryCache: ProductCodesResponse | null = null;
let productCodesInflight: Promise<ProductCodesResponse> | null = null;

/**
 * Product facet reference lists (per product line). Fetched at most once per
 * full page load; subsequent calls reuse the in-memory result.
 */
export async function getProductCodes(): Promise<ProductCodesResponse> {
  if (productCodesMemoryCache) {
    return productCodesMemoryCache;
  }
  if (productCodesInflight) {
    return productCodesInflight;
  }
  productCodesInflight = (async () => {
    const res = await AuthenticatedFetch(
      `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/reference/productCodes`
    );
    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as {
        message?: string;
      };
      throw new Error(errBody.message || res.statusText || "Request failed");
    }
    const data = (await res.json()) as ProductCodesResponse;
    productCodesMemoryCache = data;
    return data;
  })();
  try {
    return await productCodesInflight;
  } finally {
    productCodesInflight = null;
  }
}

export async function getUploadDrafts(): Promise<UploadResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/composer/uploadsInProgress`
  );
  return await res.json();
}

export async function deleteUploadDraft(uploadId: string) {
  try {
    const res = await AuthenticatedFetch(
      `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/upload/${uploadId}`,
      {
        method: "DELETE",
      }
    );
    return await res.json();
  } catch (error) {
    console.log("Error at deleteUploadDraft: ", error);
    throw error;
  }
}

export async function sendSupportEmail(email: {
  subject: string;
  message: string;
}): Promise<{ status: number } | undefined> {
  try {
    const res = await AuthenticatedFetch(
      `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/composer/sendSupportEmail`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(email),
      }
    );
    return { status: res.status };
  } catch (error) {
    console.log("Error at sendSupportEmail: ", error);
  }
}

export async function downloadSales(start: Date, end: Date, offset = 0) {
  let res: Response | undefined;
  try {
    res = await AuthenticatedFetch(
      `${
        process.env.VUE_APP_MYSCORE_BACKEND
      }/api/v1/sales/detail/download?startDateTime=${start.toISOString()}&endDateTime=${end.toISOString()}${`&offset=${offset}`}`
    );
    return res;
  } catch (error) {
    return res;
  }
}

export async function addCollection(
  newCollectionName: string,
  makeFeatured = false
): Promise<VTEXCollectionResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/collection?makeFeatured=${makeFeatured}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ displayName: newCollectionName }),
    }
  );
  const resJson = await res.json();
  if (!res.ok) {
    throw new Error(resJson.message || "Failed to create collection");
  }
  const collection: VTEXCollectionResponse = resJson.collection;
  return collection;
}

export async function deleteCollection(
  vtexCollectionId: string
): Promise<void> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/collection/${vtexCollectionId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  await res.json();
}

export async function updateCollection(
  vtexCollectionId: string,
  newCollectionName: string
): Promise<VTEXCollectionResponse> {
  const res = await AuthenticatedFetch(
    `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/collection/${vtexCollectionId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        collectionName: newCollectionName,
      }),
    }
  );

  const resJson = await res.json();
  const collection: VTEXCollectionResponse = resJson.collection;
  return collection;
}

export async function productSearchByPepperNum(
  pepperNum: string,
  allPepperProducts = false
): Promise<CollectionProduct | undefined> {
  try {
    const res = await AuthenticatedFetch(
      `${process.env.VUE_APP_MYSCORE_BACKEND}/api/v1/product/${pepperNum}?allPepperProducts=${allPepperProducts}`
    );
    if (res.ok) return await res.json();
  } catch (error) {
    return undefined;
  }
}
